import React, { useEffect, useRef, useCallback } from "react";
import { playThunder } from "../utils/soundEngine";

/**
 * Procedural Lightning Background
 * - Recursive fractal midpoint displacement branching
 * - Dual-pulse atmospheric ambient sheet lightning flash
 * - Automatic atmospheric intervals + interactive click-to-strike
 * - Pauses automatically when out of viewport for zero idle CPU/GPU cost
 */
function LightningBackground({
  darkMode = true,
  isLoaded = false,
  initialDelay = 350,
}) {
  const canvasRef = useRef(null);
  const animFrameRef = useRef(null);
  const strikesRef = useRef([]);
  const flashAlphaRef = useRef(0);
  const isVisibleRef = useRef(true);
  const nextStrikeTimerRef = useRef(null);

  // Generate a procedural branching lightning bolt
  const createBolt = useCallback(
    (startX, startY, endX, endY, branchLevel = 0) => {
      const points = [{ x: startX, y: startY }];
      const segments = 16;
      let currentX = startX;
      let currentY = startY;
      const dx = (endX - startX) / segments;
      const dy = (endY - startY) / segments;
      const roughness = (1 - branchLevel * 0.25) * 45;

      for (let i = 1; i < segments; i++) {
        currentX += dx + (Math.random() - 0.5) * roughness;
        currentY += dy + (Math.random() - 0.5) * (roughness * 0.4);
        points.push({ x: currentX, y: currentY });

        // Spawn occasional sub-branches
        if (
          branchLevel < 2 &&
          Math.random() < 0.28 &&
          i > 3 &&
          i < segments - 2
        ) {
          const subLength =
            (150 - branchLevel * 40) * (0.6 + Math.random() * 0.6);
          const subAngle = Math.atan2(dy, dx) + (Math.random() - 0.5) * 1.4;
          const subEndX = currentX + Math.cos(subAngle) * subLength;
          const subEndY = currentY + Math.sin(subAngle) * subLength;

          const subPoints = createBolt(
            currentX,
            currentY,
            subEndX,
            subEndY,
            branchLevel + 1
          );
          strikesRef.current.push({
            points: subPoints,
            alpha: 1.0,
            decay: 0.045 + Math.random() * 0.025,
            lineWidth: Math.max(1, 2.2 - branchLevel * 0.8),
            isBranch: true,
          });
        }
      }

      points.push({ x: endX, y: endY });
      return points;
    },
    []
  );

  // Trigger a full atmospheric strike
  const triggerStrike = useCallback(
    (targetX = null, targetY = null) => {
      const canvas = canvasRef.current;
      if (!canvas || !isVisibleRef.current || document.hidden) return;

      const width = canvas.width;
      const height = canvas.height;

      // Pick start & end points
      const startX = width * (0.2 + Math.random() * 0.6);
      const startY = 0;
      const endX =
        targetX !== null
          ? targetX
          : startX + (Math.random() - 0.5) * (width * 0.4);
      const endY =
        targetY !== null ? targetY : height * (0.65 + Math.random() * 0.35);

      const mainPoints = createBolt(startX, startY, endX, endY, 0);

      // Prevent accumulation of old strikes
      if (strikesRef.current.length > 3) {
        strikesRef.current = strikesRef.current.slice(-2);
      }

      strikesRef.current.push({
        points: mainPoints,
        alpha: 1.0,
        decay: 0.038 + Math.random() * 0.02,
        lineWidth: 2.8,
        isBranch: false,
      });

      // Trigger dual-pulse ambient sheet lightning flash
      flashAlphaRef.current = darkMode ? 0.22 : 0.12;

      // Atmospheric Web Audio thunder rumble
      playThunder();

      // Secondary flicker pulse
      setTimeout(() => {
        if (Math.random() < 0.7 && !document.hidden) {
          flashAlphaRef.current = Math.max(
            flashAlphaRef.current,
            darkMode ? 0.15 : 0.08
          );
        }
      }, 60 + Math.random() * 50);
    },
    [createBolt, darkMode]
  );

  // Schedule random atmospheric strikes
  const scheduleNextStrike = useCallback(() => {
    if (nextStrikeTimerRef.current) clearTimeout(nextStrikeTimerRef.current);
    if (document.hidden) return;

    // Interval between 4.5s and 9s
    const delay = 4500 + Math.random() * 4500;
    nextStrikeTimerRef.current = setTimeout(() => {
      if (isVisibleRef.current && !document.hidden) {
        triggerStrike();
      }
      scheduleNextStrike();
    }, delay);
  }, [triggerStrike]);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");

    let width = (canvas.width = canvas.parentElement.offsetWidth);
    let height = (canvas.height = canvas.parentElement.offsetHeight);

    const handleResize = () => {
      if (!canvas.parentElement) return;
      width = canvas.width = canvas.parentElement.offsetWidth;
      height = canvas.height = canvas.parentElement.offsetHeight;
    };

    window.addEventListener("resize", handleResize);

    // Initial ambient strike once loaded / entered
    let initialTimer = null;
    if (isLoaded) {
      initialTimer = setTimeout(() => {
        triggerStrike();
        scheduleNextStrike();
      }, initialDelay);
    }

    // Render loop
    const render = () => {
      if (!isVisibleRef.current) {
        animFrameRef.current = requestAnimationFrame(render);
        return;
      }

      ctx.clearRect(0, 0, width, height);

      // Render atmospheric sheet flash
      if (flashAlphaRef.current > 0.005) {
        const grad = ctx.createRadialGradient(
          width * 0.5,
          height * 0.2,
          10,
          width * 0.5,
          height * 0.5,
          Math.max(width, height)
        );

        if (darkMode) {
          grad.addColorStop(
            0,
            `rgba(180, 210, 255, ${flashAlphaRef.current * 0.9})`
          );
          grad.addColorStop(
            0.4,
            `rgba(120, 160, 255, ${flashAlphaRef.current * 0.5})`
          );
          grad.addColorStop(1, `rgba(0, 0, 0, 0)`);
        } else {
          grad.addColorStop(
            0,
            `rgba(200, 220, 255, ${flashAlphaRef.current * 0.6})`
          );
          grad.addColorStop(1, `rgba(255, 255, 255, 0)`);
        }

        ctx.fillStyle = grad;
        ctx.fillRect(0, 0, width, height);

        flashAlphaRef.current *= 0.88; // Exponential flash decay
      }

      // Render active lightning strikes
      for (let i = strikesRef.current.length - 1; i >= 0; i--) {
        const strike = strikesRef.current[i];
        if (strike.alpha <= 0.01) {
          strikesRef.current.splice(i, 1);
          continue;
        }

        const pts = strike.points;
        if (pts.length < 2) continue;

        // Outer neon glow
        ctx.save();
        ctx.beginPath();
        ctx.moveTo(pts[0].x, pts[0].y);
        for (let j = 1; j < pts.length; j++) {
          ctx.lineTo(pts[j].x, pts[j].y);
        }

        ctx.strokeStyle = darkMode
          ? `rgba(120, 175, 255, ${strike.alpha * 0.45})`
          : `rgba(90, 140, 220, ${strike.alpha * 0.35})`;
        ctx.lineWidth = strike.lineWidth * 3.2;
        ctx.lineCap = "round";
        ctx.lineJoin = "round";
        ctx.shadowColor = darkMode
          ? "rgba(160, 210, 255, 0.8)"
          : "rgba(100, 150, 230, 0.4)";
        ctx.shadowBlur = 18;
        ctx.stroke();

        // Inner bright core
        ctx.beginPath();
        ctx.moveTo(pts[0].x, pts[0].y);
        for (let j = 1; j < pts.length; j++) {
          ctx.lineTo(pts[j].x, pts[j].y);
        }
        ctx.strokeStyle = darkMode
          ? `rgba(255, 255, 255, ${strike.alpha * 0.95})`
          : `rgba(230, 240, 255, ${strike.alpha * 0.9})`;
        ctx.lineWidth = strike.lineWidth;
        ctx.shadowBlur = 6;
        ctx.shadowColor = "#ffffff";
        ctx.stroke();
        ctx.restore();

        // Decay alpha
        strike.alpha -= strike.decay;
      }

      animFrameRef.current = requestAnimationFrame(render);
    };

    animFrameRef.current = requestAnimationFrame(render);

    // Pause when hero is scrolled out of view
    const observer = new IntersectionObserver(
      ([entry]) => {
        isVisibleRef.current = entry.isIntersecting;
      },
      { threshold: 0.1 }
    );

    if (canvas.parentElement) {
      observer.observe(canvas.parentElement);
    }

    // Tab visibility handler: pause and purge strikes when tab is hidden
    const handleVisibilityChange = () => {
      if (document.hidden) {
        strikesRef.current = [];
        flashAlphaRef.current = 0;
        if (nextStrikeTimerRef.current)
          clearTimeout(nextStrikeTimerRef.current);
      } else if (isVisibleRef.current) {
        // Tab restored: schedule normal strike
        scheduleNextStrike();
      }
    };
    document.addEventListener("visibilitychange", handleVisibilityChange);

    // Interactive click-to-strike handler on Hero section
    const handleParentClick = (e) => {
      if (
        e.target.closest(
          "a, button, [role='button'], input, textarea, select, label"
        )
      ) {
        return;
      }
      const rect = canvas.getBoundingClientRect();
      const clickX = e.clientX - rect.left;
      const clickY = e.clientY - rect.top;
      triggerStrike(clickX, clickY);
    };

    const parent = canvas.parentElement;
    if (parent) {
      parent.addEventListener("click", handleParentClick);
    }

    return () => {
      window.removeEventListener("resize", handleResize);
      document.removeEventListener("visibilitychange", handleVisibilityChange);
      if (parent) {
        parent.removeEventListener("click", handleParentClick);
      }
      if (initialTimer) clearTimeout(initialTimer);
      if (nextStrikeTimerRef.current) clearTimeout(nextStrikeTimerRef.current);
      if (animFrameRef.current) cancelAnimationFrame(animFrameRef.current);
      observer.disconnect();
    };
  }, [triggerStrike, scheduleNextStrike, darkMode, isLoaded, initialDelay]);

  return (
    <canvas
      ref={canvasRef}
      className="absolute inset-0 w-full h-full pointer-events-none z-0 overflow-hidden mix-blend-screen opacity-90 transition-opacity duration-700"
      style={{ willChange: "transform, opacity" }}
    />
  );
}

export default LightningBackground;
